import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type MouseEvent } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useNavigate } from 'react-router'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { UserLabel } from '@/components/ui/user-label'
import { CacheKeys } from '../../../../lib/cache'
import { staffUserHref } from '../../../../models/staffUser'
import {
  staffUserName,
  toggleLeadAssignments,
  toggleStaffUser,
  useCurrentUser,
  useStaffUsers,
} from '../../../../models/user'

export function StaffUsers() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useCurrentUser({ enabled: true })
  const { staffUsers, isLoading } = useStaffUsers()

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.STAFF_USERS] })
  }

  const toggleMutation = useMutation({
    mutationFn: ({ userId, isStaff }: { userId: string; isStaff: boolean }) =>
      toggleStaffUser(userId, isStaff),
    onSuccess: invalidate,
  })

  const leadRotationMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      toggleLeadAssignments(userId, enabled),
    onSuccess: invalidate,
  })

  function openInNewTab(userId: string) {
    window.open(staffUserHref(userId), '_blank', 'noopener,noreferrer')
  }

  // Rows aren't anchors, so modifier- and middle-clicks open a new tab by hand.
  function handleRowClick(e: MouseEvent, userId: string) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) openInNewTab(userId)
    else navigate(staffUserHref(userId))
  }

  const isCurrentUserStaff = user?.isStaff
  const canEditRotation = user?.isCaiAdmin
  // Name, Email, Cai Labs, Lead rotation — plus Staff for staff users.
  const columnCount = isCurrentUserStaff ? 5 : 4

  return (
    <>
      <Helmet>
        <title>Staff Users</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div className="mb-2 text-2xl font-semibold">Staff Users</div>
        <Card>
          <CardHeader>
            <CardTitle>Staff Users</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Cai Labs</TableHead>
                  {isCurrentUserStaff && <TableHead className="font-semibold">Staff</TableHead>}
                  <TableHead className="font-semibold">Lead rotation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableSkeletonRows columns={columnCount} />}
                {!isLoading && staffUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount} className="pt-5 text-center">
                      No staff users found
                    </TableCell>
                  </TableRow>
                )}
                {staffUsers.map((staffUser) => (
                  <TableRow
                    key={staffUser.id}
                    className="cursor-pointer"
                    onClick={(e) => handleRowClick(e, staffUser.id)}
                    onAuxClick={(e) => {
                      if (e.button === 1) {
                        e.preventDefault()
                        openInNewTab(staffUser.id)
                      }
                    }}
                  >
                    <TableCell className="font-medium">
                      {/* The row's handlers would open it a second time. */}
                      <Link
                        to={staffUserHref(staffUser.id)}
                        className="inline-flex rounded-sm hover:underline"
                        onClick={(e) => e.stopPropagation()}
                        onAuxClick={(e) => e.stopPropagation()}
                      >
                        <UserLabel
                          name={staffUserName(staffUser)}
                          src={staffUser.avatar}
                          avatarClassName="size-7"
                          fallbackClassName="text-xs"
                        />
                      </Link>
                    </TableCell>
                    <TableCell>{staffUser.email}</TableCell>
                    <TableCell>{staffUser.isCaiAdmin ? 'Yes' : 'No'}</TableCell>
                    {isCurrentUserStaff && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={staffUser.isStaff}
                          onCheckedChange={(checked) =>
                            toggleMutation.mutate({
                              userId: staffUser.id,
                              isStaff: checked,
                            })
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={staffUser.receivesLeadAssignments}
                        disabled={!canEditRotation}
                        onCheckedChange={(checked) =>
                          leadRotationMutation.mutate({
                            userId: staffUser.id,
                            enabled: checked,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
